/** Setup steps: address, administrator and localization. */
import { CURRENCIES, type Locale } from "@offertli/shared";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Select } from "@primereact/ui/select";
import { translateError } from "../../lib/form.js";
import { PASSWORD_MIN, TIMEZONES, type SetupForm, useSetup } from "./wizard.js";

export function AddressStep() {
  const { t, register, setValue, errors, values, countryChoices, countryName } =
    useSetup();
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field
        label={t("setup.address.line1")}
        htmlFor="address1"
        error={translateError(errors.company?.addressLine1?.message, t)}
        required
        className="sm:col-span-2"
      >
        <Input
          id="address1"
          autoComplete="address-line1"
          invalid={!!errors.company?.addressLine1}
          {...register("company.addressLine1")}
        />
      </Field>
      <Field
        label={t("setup.address.line2")}
        htmlFor="address2"
        className="sm:col-span-2"
      >
        <Input
          id="address2"
          autoComplete="address-line2"
          {...register("company.addressLine2")}
        />
      </Field>
      <Field
        label={t("setup.address.postalCode")}
        htmlFor="postal-code"
        error={translateError(errors.company?.postalCode?.message, t)}
        required
      >
        <Input
          id="postal-code"
          autoComplete="postal-code"
          invalid={!!errors.company?.postalCode}
          {...register("company.postalCode")}
        />
      </Field>
      <Field
        label={t("setup.address.city")}
        htmlFor="city"
        error={translateError(errors.company?.city?.message, t)}
        required
      >
        <Input
          id="city"
          autoComplete="address-level2"
          invalid={!!errors.company?.city}
          {...register("company.city")}
        />
      </Field>
      <Field
        label={t("setup.address.canton")}
        htmlFor="canton"
        error={translateError(errors.company?.canton?.message, t)}
      >
        <Input id="canton" {...register("company.canton")} />
      </Field>
      <Field
        label={t("setup.address.country")}
        htmlFor="country"
        error={translateError(errors.company?.country?.message, t)}
        required
      >
        <Select.Root
          id="country"
          invalid={!!errors.company?.country}
          value={values.company.country}
          onValueChange={(event: { value: unknown }) =>
            setValue("company.country", event.value as string, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          fluid
          options={[
            ...countryChoices.map((code) => ({
              value: code,
              label: countryName(code),
            })),
          ]}
          optionLabel="label"
          optionValue="value"
        >
          <Select.Trigger type="button" aria-label={t("setup.address.country")}>
            <Select.Value />
            <Select.Indicator className="pi pi-chevron-down" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner>
              <Select.Popup>
                <Select.List />
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </Field>
      <Field
        label={t("setup.address.email")}
        htmlFor="company-email"
        error={translateError(errors.company?.email?.message, t)}
        required
      >
        <Input
          id="company-email"
          type="email"
          invalid={!!errors.company?.email}
          {...register("company.email")}
        />
      </Field>
      <Field
        label={t("setup.address.phone")}
        htmlFor="company-phone"
        error={translateError(errors.company?.phone?.message, t)}
      >
        <Input
          id="company-phone"
          type="tel"
          autoComplete="tel"
          {...register("company.phone")}
        />
      </Field>
    </div>
  );
}

export function SystemAdminStep() {
  const { t, register, errors } = useSetup();
  return (
    <div className="space-y-4">
      <p className="text-sm text-app-muted">
        {t("setup.systemAdmin.description")}
      </p>
      <div className="rounded-lg border border-app-border bg-app-background p-3 text-sm">
        <span className="text-app-muted">
          {t("setup.systemAdmin.username")}
        </span>
        <strong className="ml-2 font-mono">admin</strong>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t("common.password")}
          htmlFor="system-admin-password"
          error={
            errors.systemAdmin?.password?.message
              ? t(errors.systemAdmin.password.message, {
                  min: PASSWORD_MIN,
                })
              : undefined
          }
          required
        >
          <Input
            id="system-admin-password"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.systemAdmin?.password}
            {...register("systemAdmin.password")}
          />
        </Field>
        <Field
          label={t("invite.confirmPassword")}
          htmlFor="system-admin-confirm"
          error={translateError(
            errors.systemAdmin?.confirmPassword?.message,
            t,
          )}
          required
        >
          <Input
            id="system-admin-confirm"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.systemAdmin?.confirmPassword}
            {...register("systemAdmin.confirmPassword")}
          />
        </Field>
      </div>
    </div>
  );
}

export function LocalizationStep() {
  const { t, setValue, values, localeChoices, documentLocaleChanged } =
    useSetup();
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field
        label={t("setup.localization.locale")}
        htmlFor="default-locale"
        hint={t("setup.localization.localeHint")}
        required
      >
        <Select.Root
          id="default-locale"
          value={values.localization.defaultLocale}
          onValueChange={(event: { value: unknown }) => {
            documentLocaleChanged.current = true;
            setValue("localization.defaultLocale", event.value as Locale, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          fluid
          options={[
            ...localeChoices.map((locale) => ({
              value: locale,
              label: t(`locale.${locale}`),
            })),
          ]}
          optionLabel="label"
          optionValue="value"
        >
          <Select.Trigger
            type="button"
            aria-label={t("setup.localization.locale")}
          >
            <Select.Value />
            <Select.Indicator className="pi pi-chevron-down" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner>
              <Select.Popup>
                <Select.List />
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </Field>
      <Field
        label={t("setup.localization.timezone")}
        htmlFor="timezone"
        required
      >
        <Select.Root
          id="timezone"
          value={values.localization.timezone}
          onValueChange={(event: { value: unknown }) =>
            setValue("localization.timezone", event.value as string, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          fluid
          options={[
            ...TIMEZONES.map((tz) => ({
              value: tz,
              label: tz,
            })),
          ]}
          optionLabel="label"
          optionValue="value"
        >
          <Select.Trigger
            type="button"
            aria-label={t("setup.localization.timezone")}
          >
            <Select.Value />
            <Select.Indicator className="pi pi-chevron-down" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner>
              <Select.Popup>
                <Select.List />
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </Field>
      <Field
        label={t("setup.localization.currency")}
        htmlFor="currency"
        required
      >
        <Select.Root
          id="currency"
          value={values.localization.currency}
          onValueChange={(event: { value: unknown }) =>
            setValue(
              "localization.currency",
              event.value as SetupForm["localization"]["currency"],
              {
                shouldDirty: true,
                shouldValidate: true,
              },
            )
          }
          fluid
          options={[
            ...CURRENCIES.map((currency) => ({
              value: currency,
              label: currency,
            })),
          ]}
          optionLabel="label"
          optionValue="value"
        >
          <Select.Trigger
            type="button"
            aria-label={t("setup.localization.currency")}
          >
            <Select.Value />
            <Select.Indicator className="pi pi-chevron-down" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner>
              <Select.Popup>
                <Select.List />
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </Field>
    </div>
  );
}
